/**
 * PolicyNumberCombobox — forced-selection combobox for the launch form's
 * `policy_number` field, rendered via JsonSchemaForm's `fieldOverrides` when
 * the server resolved `policy_options` for the selected client.
 *
 * Contract (operator decision, 2026-07-21):
 *   - The committed value ONLY ever comes from selecting an option — typing
 *     filters the list but free text can never be left in the field (MUI
 *     Autocomplete without freeSolo reverts uncommitted text on blur).
 *   - Each option row shows the policy number plus, right-aligned, the term
 *     (effective – expiration, `MM-DD-YYYY`) and the product group / policy type.
 *   - The committed value shows that SAME term beside the number, so the
 *     operator can tell which term is selected without reopening the list.
 *   - Rows render oldest-effective first: current in-force term at the top,
 *     future renewals below (display order only — see
 *     `sortPolicyOptionsForDisplay`).
 *   - Selecting an option also drives `policy_type`, `carrier_name` and
 *     `carrier_id` from the SAME row (the caller wires that in onSelect).
 *   - Rows are identified by TERM (`policyTermKey`), never by policy number:
 *     one policy's current term and its future renewal share a number, and the
 *     operator must be able to select either.
 *
 * Built on MUI Autocomplete to match the house combobox
 * (ClientSearchDropdown — the canonical source of this theme; copied here
 * because the theme object isn't exported, per ui/CLAUDE.md parity rules).
 * Only mounted when options.length >= 1 — workflows / clients without
 * resolved policy options keep the schema's plain free-text input.
 */
import { useMemo, useRef } from "react";
import {
  Autocomplete,
  TextField,
  ThemeProvider,
  createTheme,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material";
import type { PolicyInForce } from "~/common/workflows/types/client";
import FieldHelpTooltip from "~/common/workflows/launch/FieldHelpTooltip";
import {
  formatTermDate,
  policyTermKey,
  resolveSelectedPolicy,
  sortPolicyOptionsForDisplay,
} from "./policyPrefillOptions";

export const POLICY_NUMBER_HELP =
  "Type to filter, then pick one of this client's policies — the carrier and policy type follow your selection.";

// Theme copied from ClientSearchDropdown (app/common/workflows/components) —
// same paper/listbox/option/focus-glow styling so the two comboboxes read as
// one control family.
//
// MUI theme values are colours, not class names, and this component is now
// bundled by BOTH ora and the Outlook add-in — importing either app's token
// module (ora's getColorHexCodeByColorName / the add-in's src/tokens.ts) would
// pull that app's Tailwind config into the other's bundle. So the hexes stay
// literal here, exactly as ClientSearchDropdown already does it; each value is
// copied verbatim from the add-in's src/tokens.ts and named in the comment
// beside it, so the rendered result is pixel-identical to the pre-move build.
//   #EAECF0 primary-gray-light-200   #D0D5DD primary-gray-light-300
//   #101828 primary-gray-light-900   #FFF3A5 lemon
//   #FFA400 blossom
// #FFF8C9 (option hover) is the one value with no token — same shade the
// dropdown family uses in ora's dropdownTheme.ts.
const SHADOW_LG =
  "0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)";
const FOCUS_GLOW = "0px 0px 2px 2px rgba(255, 164, 0, 0.24)";

// The AMBIENT theme is the base, read with useTheme() rather than imported:
// the add-in wraps the pane in <ThemeProvider theme={theme}> (src/main.tsx) and
// ora in app/mui/MuiProvider.tsx, so each host supplies its own base and this
// component only layers the combobox overrides on top.
const buildPolicyComboTheme = (base: Theme) =>
  createTheme(base, {
    components: {
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            borderRadius: "0.5rem",
            border: "1px solid #EAECF0", // primary-gray-light-200
            boxShadow: SHADOW_LG,
          },
          listbox: {
            marginTop: "5px",
            padding: "6px",
            "&::-webkit-scrollbar": { width: "8px" },
            // #EAECF0 primary-gray-light-200
            "&::-webkit-scrollbar-thumb": { borderRadius: "10px", background: "#EAECF0" },
            "&::-webkit-scrollbar-track": { borderRadius: "10px" },
          },
          option: {
            backgroundColor: "transparent !important",
            "&:hover": { backgroundColor: "#FFF8C9 !important" },
            // #FFF3A5 lemon
            "&[aria-selected='true']": { backgroundColor: "#FFF3A5 !important" },
            "&.Mui-focused": { backgroundColor: "#FFF8C9 !important" },
          },
          inputRoot: {
            paddingLeft: "8px !important",
            paddingRight: "8px !important",
            paddingTop: "8px !important",
            paddingBottom: "8px !important",
          },
          // NOTE: endAdornment is left completely untouched (MUI default), so the
          // clear/expand icons sit exactly where they do in ClientSearchDropdown.
          // The selected term is NOT injected into it — see renderInput below.
          // History (2026-07-28): injecting the term into endAdornment was tried
          // twice — `position: static` made the container a flex item that grew
          // past the field (clipped term, caret on the border), and the default
          // absolute container stacked the term underneath the icons. The term is
          // now positioned independently instead of fighting that container.
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "& fieldset": { borderRadius: "0.5rem !important" },
            "&.Mui-focused fieldset": {
              borderColor: "#D0D5DD !important", // primary-gray-light-300
              borderStyle: "solid",
              borderWidth: "1px",
              boxShadow: `${FOCUS_GLOW} !important`,
            },
            "&:hover fieldset": {
              borderColor: "#D0D5DD !important", // primary-gray-light-300
              borderStyle: "solid",
              borderWidth: "1px",
              boxShadow: `${FOCUS_GLOW} !important`,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: { input: { fontWeight: "normal", fontSize: "0.875rem" } },
      },
      MuiPopper: {
        styleOverrides: { root: { marginTop: "5px" } },
      },
    },
  });

export interface PolicyNumberComboboxProps {
  /** Field label (from the schema — e.g. "Policy Number"). */
  label: string;
  required?: boolean;
  /** Committed value (values.policy_number). */
  value: string;
  /**
   * Committed TERM identity (values.policy_term_key) — names which term of
   * `value` is selected when a policy's current term and its future renewal
   * share one number. Empty until the operator picks (the server's prefill can
   * only send a number), which resolves to the current term.
   */
  termKey?: string;
  /** The client's resolved in-force policy options (normalized rows). */
  options: readonly PolicyInForce[];
  /** Commit a selection — receives the full option row. */
  onSelect: (option: PolicyInForce) => void;
  /** Clear the committed value (clear button, or emptied field + blur). */
  onClear?: () => void;
  disabled?: boolean;
  /**
   * Newest-effective first instead of the default oldest-first. The default suits
   * the mortgagee launch, whose options are in-force terms only (the current term
   * heads the list). The quote-compare launcher also offers EXPIRED policies, where
   * oldest-first puts years-old policies on top (operator, 2026-09-23).
   */
  newestFirst?: boolean;
}

const squash = (s: string) => s.replace(/\s+/g, "").toUpperCase();

/**
 * Geometry for the selected-term label, in rem.
 *
 * `TERM_RIGHT_REM` clears MUI's icon cluster (clear + expand). MUI hides the
 * clear button with `visibility: hidden` rather than unmounting it, so that
 * cluster's width is CONSTANT whether or not the field is hovered — which is
 * what makes a fixed offset safe here.
 *
 * The label is positioned against the field wrapper rather than injected into
 * MUI's endAdornment: that container is absolutely right-anchored and stacked
 * the label under the icons, and forcing it to `static` made it grow past the
 * field edge. Positioning independently avoids both failure modes.
 */
const TERM_RIGHT_REM = 4.25;
const TERM_MAX_REM = 9;

/**
 * Right-side term detail: "effective – expiration", each `MM-DD-YYYY`.
 * Shown on every option row AND next to the committed value, so the operator can
 * see WHICH term is selected without reopening the list. Exported for tests.
 */
export function termLabel(p: PolicyInForce): string {
  const eff = formatTermDate(p.effective_date);
  const exp = formatTermDate(p.expiration_date);
  if (eff && exp) return `${eff} – ${exp}`;
  return eff || exp || "";
}

/**
 * Space/case-insensitive filter over number + product group; when the input
 * still shows the committed value, return ALL options so the user can switch
 * without clearing first. Pure — exported for tests (jsdom can't simulate
 * typing into a MUI Autocomplete in this workspace, so the logic is verified
 * here and the DOM glue in the browser).
 */
export function filterPolicyOptions(
  options: readonly PolicyInForce[],
  typed: string,
  committed: string,
): PolicyInForce[] {
  const q = squash(typed);
  if (!q || typed === committed) return [...options];
  return options.filter((o) =>
    squash(
      `${o.policy_number ?? ""} ${o.product_group ?? o.policy_type ?? ""}`,
    ).includes(q),
  );
}

/**
 * Blur resolution for the forced-selection contract. Pure — exported for
 * tests. Returns:
 *   - "clear"  → the user emptied a previously-committed field
 *   - a row    → the typed text exactly (space-insensitively) matches EXACTLY
 *                ONE option that isn't already committed → counts as a pick
 *   - null     → nothing to do (MUI reverts uncommitted text on its own)
 *
 * An ambiguous number — several terms of one policy — never auto-commits on
 * blur: the typed text cannot say WHICH term, so the operator has to pick a row.
 */
export function resolveBlurAction(
  options: readonly PolicyInForce[],
  typed: string,
  committed: string,
  committedTermKey = "",
): "clear" | PolicyInForce | null {
  const trimmed = typed.trim();
  if (trimmed === "") return committed !== "" ? "clear" : null;
  const matches = options.filter(
    (o) => o.policy_number && squash(o.policy_number) === squash(trimmed),
  );
  if (matches.length !== 1) return null;
  const exact = matches[0];
  if (policyTermKey(exact) === committedTermKey) return null;
  if (exact.policy_number !== committed) return exact;
  return null;
}

export function PolicyNumberCombobox({
  label,
  required = false,
  value,
  termKey = "",
  options,
  onSelect,
  onClear,
  disabled = false,
  newestFirst = false,
}: PolicyNumberComboboxProps): JSX.Element {
  // The host's ambient theme is the base for the combobox overrides. Read here
  // rather than imported at module scope so the same component picks up the
  // add-in's theme in the pane and ora's in the web app (ui/CLAUDE.md parity).
  const ambient = useTheme();
  const policyComboTheme = useMemo(
    () => buildPolicyComboTheme(ambient),
    [ambient],
  );
  // Resolve by TERM, not by number — `options.find(o => o.policy_number ===
  // value)` returned whichever term sorted first (the future renewal, since
  // options arrive expiration-DESC), so picking the current term snapped the
  // selection back to the renewal.
  const selected = useMemo(
    () => resolveSelectedPolicy(options, value, termKey),
    [options, value, termKey],
  );
  // Cached analyze_email can seed a policy number before `policy_options`
  // arrive. Show that number rather than an empty combobox while the
  // background prefill is still filling the option list.
  const displayed = useMemo(() => {
    if (selected) return selected;
    if (!value) return null;
    return {
      policy_line_sk: 0,
      policy_type: "",
      expiration_date: "",
      expiring_premium: null,
      renewal_likelihood: "N/A",
      status: "active",
      policy_number: value,
      effective_date: null,
      product_group: null,
      carrier_id: null,
      carrier_name: null,
    } satisfies PolicyInForce;
  }, [selected, value]);
  // Oldest-effective first, so the current in-force term heads the list and
  // future renewals fall below it. Display-only — `selected` above resolves by
  // term key, never by position, so reordering cannot change the selection.
  const optionsList = useMemo(() => {
    const sorted = sortPolicyOptionsForDisplay(options);
    return newestFirst ? sorted.reverse() : sorted;
  }, [options, newestFirst]);
  // Track what's typed (ref, not state — the input itself stays MUI-managed)
  // so blur can treat an exact (space-insensitive) match as a pick and an
  // emptied field as a clear. Everything else reverts to the committed value
  // (MUI's default for non-freeSolo autocompletes).
  const typedRef = useRef(value);
  const selectedTerm = selected ? termLabel(selected) : "";

  const handleBlur = () => {
    const action = resolveBlurAction(options, typedRef.current, value, termKey);
    if (action === "clear") onClear?.();
    else if (action) onSelect(action);
  };

  return (
    <div className="flex flex-col gap-1" data-testid="policy-number-combobox">
      <label
        htmlFor="policy-number-combobox-input"
        className="flex items-center gap-1.5 text-xs font-bold text-primary-gray-light-700"
      >
        {label}
        {required && <span className="text-primary-error-600 ms-1">*</span>}
        <FieldHelpTooltip
          text={POLICY_NUMBER_HELP}
          testId="policy-number-help"
        />
      </label>
      <ThemeProvider theme={policyComboTheme}>
        {/* `relative` anchors the selected-term label (below) to the field. */}
        <div className="relative">
          <Autocomplete<PolicyInForce, false, false, false>
            value={displayed}
            size="small"
            disabled={disabled}
            openOnFocus
            autoHighlight
            options={optionsList}
            getOptionLabel={(o) => o.policy_number ?? ""}
            isOptionEqualToValue={(o, v) => policyTermKey(o) === policyTermKey(v)}
            filterOptions={(opts, state) =>
              filterPolicyOptions(opts, state.inputValue, value)
            }
            onChange={(_e, nv) => {
              if (nv) onSelect(nv);
              else onClear?.();
            }}
            onInputChange={(_e, v) => {
              typedRef.current = v;
            }}
            noOptionsText="No matching policy — clear the field to see all options."
            renderOption={(props, o) => (
              <li
                {...props}
                key={policyTermKey(o)}
                className="mx-1 flex touch-target cursor-pointer items-baseline justify-between gap-3 rounded-md px-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-primary-gray-light-900">
                  {o.policy_number}
                </span>
                <span className="flex-shrink-0 text-2xs text-primary-gray-light-500">
                  {termLabel(o)}
                  {(o.product_group ?? o.policy_type) &&
                    ` · ${o.product_group ?? o.policy_type}`}
                </span>
              </li>
            )}
            className="w-full"
            renderInput={(params) => (
              <TextField
                {...params}
                id="policy-number-combobox-input"
                placeholder="Select a policy…"
                variant="outlined"
                // Reserve room on the right for the term label + the icon cluster
                // so a long policy number's text stops before them instead of
                // running underneath. Only while a term is actually shown.
                sx={
                  selectedTerm
                    ? {
                        "& .MuiAutocomplete-input": {
                          paddingRight: `${TERM_RIGHT_REM + TERM_MAX_REM}rem !important`,
                        },
                      }
                    : undefined
                }
                inputProps={{
                  ...params.inputProps,
                  // Run MUI's own blur handling (revert/highlight bookkeeping)
                  // first, then our exact-match / clear logic on the typed text.
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                    params.inputProps.onBlur?.(e);
                    handleBlur();
                  },
                }}
              />
            )}
          />
          {/* Selected term, right-aligned and clear of the icon cluster.
              Deliberately NOT folded into getOptionLabel: that string is also the
              input's text value, which `filterPolicyOptions` compares against the
              committed value to decide "show all options", and which
              `resolveBlurAction` matches against policy numbers — dates in it
              would silently break both. `pointer-events-none` keeps it from
              swallowing a click aimed at the caret. */}
          {selectedTerm && (
            <span
              data-testid="policy-number-selected-term"
              className="pointer-events-none absolute top-1/2 -translate-y-1/2 truncate text-right text-2xs text-primary-gray-light-500"
              style={{ right: `${TERM_RIGHT_REM}rem`, maxWidth: `${TERM_MAX_REM}rem` }}
            >
              {selectedTerm}
            </span>
          )}
        </div>
      </ThemeProvider>
    </div>
  );
}

export default PolicyNumberCombobox;
